import React from 'react';
const OvTutorial = () => {
    const sectionStyle = { marginBottom: '24px' };
    const headingStyle = {
        fontSize: '20px',
        fontWeight: 'bold',
        marginBottom: '8px',
    };
    const subheadingStyle = {
        fontSize: '18px',
        fontWeight: 'bold',
        marginTop: '16px',
        marginBottom: '8px',
    };
    const listStyle = { paddingLeft: '20px', marginBottom: '12px' };
    const tableStyle = {
        borderCollapse: 'collapse',
        width: '100%',
        marginBottom: '20px',
    };
    const thStyle = {
        border: '1px solid #ccc',
        padding: '8px',
        fontWeight: 'bold',
        backgroundColor: '#f2f2f2',
    };
    const tdStyle = {
        border: '1px solid #ccc',
        padding: '8px',
    };
    return (
        <div
            style={{
                padding: '24px',
                fontFamily: 'Arial, sans-serif',
                fontSize: '14px',
            }}
        >
            <h1
                style={{
                    fontSize: '24px',
                    fontWeight: 'bold',
                    marginBottom: '16px',
                }}
            >
                Purpose of this Guide
            </h1>
            <p style={sectionStyle}>
                This document provides detailed guidelines on how to correctly
                fill in the Master Recipe File for uploading recipes into the
                CMS. The Excel file must include the following three sheets:{' '}
                <strong>recipes</strong>, <strong>ingredients</strong>, and{' '}
                <strong>steps</strong>. Additionally, this guide incorporates
                important updates from the backend discussion regarding
                appliance generations and mandatory fields.
            </p>

            <div style={sectionStyle}>
                <h2 style={headingStyle}>
                    Sheet: recipes – General Recipe Data
                </h2>
                <p>
                    Follow the table fields as per the format below. Values must
                    match the dictionary when specified.
                </p>
                <p>
                    Please refer to the Excel template for full column
                    descriptions. Key highlights:
                </p>
                <ul style={listStyle}>
                    <li>
                        Use sequential numeric values for{' '}
                        <code>'recipeid'</code> and format <code>'id'</code> as
                        recipeid_portion_OV (e.g., <strong>05_04_OV</strong>).
                    </li>
                    <li>
                        <strong>brand:</strong> use only approved values (Haier,
                        Candy, etc.). If multiple, separate with{' '}
                        <strong>commas</strong>.
                    </li>
                    <li>
                        Use <strong>ENGLISH</strong> only for free text fields
                        such as <strong>'title'</strong> and{' '}
                        <strong>'description'</strong>.
                    </li>
                    <li>
                        Boolean fields (
                        <strong>probe, steam, keepheat...</strong>) must be
                        'Yes' or 'No'.
                    </li>
                    <li>
                        Use codes like <code>RECIPES.COURSE.DESSERT</code>,{' '}
                        <code>RECIPES.MAIN_INGREDIENT.MEAT</code>, etc., only.
                    </li>
                </ul>
            </div>

            <div style={sectionStyle}>
                <h2 style={headingStyle}>
                    3. Sheet: steps – Cooking Steps and Parameters
                </h2>
                <ul style={listStyle}>
                    <li>
                        <code>'appliance'</code> should be{' '}
                        <code>'RECIPES.APPLIANCE.OVEN'</code> where technical
                        parameters are used.
                    </li>
                    <li>
                        <code>'cooking_mode'</code>, <code>'temperature'</code>,{' '}
                        <code>'time'</code>, <code>'temperature_probe'</code>,
                        and <code>'preheating'</code> should be included for
                        each generation.
                    </li>
                    <li>
                        <code>prCodeCata</code>, <code>prCodeVision</code>,{' '}
                        <code>prCodePyro</code> must be left blank.
                    </li>
                </ul>
            </div>

            <div style={sectionStyle}>
                <h2 style={headingStyle}>✅ Oven Model Generations</h2>
                <table style={tableStyle}>
                    <thead>
                        <tr>
                            <th style={thStyle}>Generation</th>
                            <th style={thStyle}>Models</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td style={tdStyle}>
                                <strong>GEN1</strong>
                            </td>
                            <td style={tdStyle}>
                                <code>
                                    tad, tft, elettronicaZero, preco,
                                    fullTouch2.0, chefAtHome
                                </code>
                            </td>
                        </tr>
                        <tr>
                            <td style={tdStyle}>
                                <strong>GEN2</strong>
                            </td>
                            <td style={tdStyle}>
                                <code>fullTouch2.0Evo</code>
                            </td>
                        </tr>
                        <tr>
                            <td style={tdStyle}>
                                <strong>GEN3</strong>
                            </td>
                            <td style={tdStyle}>
                                <code>basic, graphic, color, premium</code>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div style={sectionStyle}>
                <h2 style={subheadingStyle}>Column Mapping by Generation</h2>
                <table style={tableStyle}>
                    <thead>
                        <tr>
                            <th style={thStyle}>Generation</th>
                            <th style={thStyle}>
                                Cooking Parameters Location in{' '}
                                <code>steps</code> Sheet
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td style={tdStyle}>
                                <strong>GEN1</strong>
                            </td>
                            <td style={tdStyle}>
                                Columns <strong>G to L</strong> (highlighted in
                                blue)
                            </td>
                        </tr>
                        <tr>
                            <td style={tdStyle}>
                                <strong>GEN2</strong>
                            </td>
                            <td style={tdStyle}>
                                Columns <strong>M to R</strong> (highlighted in
                                orange)
                            </td>
                        </tr>
                        <tr>
                            <td style={tdStyle}>
                                <strong>GEN3</strong>
                            </td>
                            <td style={tdStyle}>
                                Columns <strong>S to Y</strong> (highlighted in
                                violet)
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div style={sectionStyle}>
                <h2 style={subheadingStyle}>Cooking Mode Usage</h2>
                <ul style={listStyle}>
                    <li>
                        For <strong>GEN1</strong> and <strong>GEN2</strong>, the{' '}
                        <code>cooking_mode</code> must be entered in{' '}
                        <strong>column E</strong>.
                    </li>
                    <li>
                        For <strong>GEN3</strong>, the <code>cooking_mode</code>{' '}
                        must be entered in <strong>column S</strong>.
                    </li>
                </ul>
            </div>

            <div style={sectionStyle}>
                <h2 style={headingStyle}>
                    4. Sheet: ingredients – Ingredients per Recipe
                </h2>
                <ul style={listStyle}>
                    <li>
                        <code>'recipe_id'</code> must match the one in the
                        recipes sheet.
                    </li>
                    <li>
                        Use only dictionary values for <code>'unit'</code>.
                    </li>
                    <li>
                        <code>'ingredient_id'</code> must be in uppercase with
                        underscores.
                    </li>
                </ul>
            </div>

            <div style={sectionStyle}>
                <h2 style={headingStyle}>5. General Rules & Warnings</h2>
                <ul style={listStyle}>
                    <li>⚠️ Do not change column order or sheet names.</li>
                    <li>
                        🟡 Highlight in yellow any new tag, unit, or ingredient
                        not found in the current dictionary.
                    </li>
                    <li>
                        📎 Include a filled example row for each sheet in the
                        template file.
                    </li>
                    <li>
                        🔗 All values should follow coding conventions exactly
                        as in the dictionary (e.g.,{' '}
                        <code>RECIPES.COURSE.FIRST_COURSE</code>).
                    </li>
                </ul>
            </div>
        </div>
    );
};

export default OvTutorial;
